import React, { useState, useEffect } from 'react';
import { X, Link2, Check, Copy, ArrowUpRight } from 'lucide-react';
import { useToast } from './ui/Toast';
import { getStoreUrl } from '../config/platformConfig';
import './StoreActivationSidebar.css';

/**
 * Compact "your store is live" share card.
 *
 * Previously rendered as a full-height side drawer with a heavy
 * gradient icon tile and three full-width labeled share buttons
 * (which broke into squeezed, clipped pills at narrow widths). The
 * new design is a small bottom-anchored card that doesn't block
 * the dashboard:
 *
 *   - Mobile: bottom sheet with horizontal margin, slides up from
 *     the bottom edge.
 *   - Desktop (>=640px): floating card pinned to the bottom-right.
 *
 * No backdrop scrim — it's an ambient nudge, not a modal. The
 * Dashboard's trigger logic (only show when store < 30 days old,
 * respect localStorage dismiss flags) is unchanged.
 *
 * Component API kept stable so Dashboard.js doesn't need rewiring:
 *   props: { store, onClose }
 */

const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const TwitterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const StoreActivationSidebar = ({ store, onClose }) => {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Animate in on the next frame so the initial render establishes
  // the off-screen state, then the visible class triggers the
  // transition. Single-frame delay is enough — no setTimeout.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  if (!store) return null;

  const storeUrl = store.slug ? getStoreUrl(store.slug) : '';
  const storeName = store.name || 'my store';

  // Trim http(s):// for display so the URL fits in the compact pill.
  const displayUrl = storeUrl.replace(/^https?:\/\//, '');

  const shareMessage = `Hey there, I just created my store on Revstar. You can now order from my store: ${storeUrl}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
  const twitterUrl  = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(storeUrl)}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(storeUrl);
      setCopied(true);
      toast.success('Store link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  // Two dismiss modes — keep the same localStorage contract as
  // before so Dashboard.js's trigger logic still works.
  const closeWithAnimation = (afterUnmount) => {
    setIsVisible(false);
    setTimeout(() => {
      if (typeof afterUnmount === 'function') afterUnmount();
      onClose?.();
    }, 280);
  };

  const handleClose = () => {
    // X button = "don't show again" — permanent dismiss.
    closeWithAnimation(() => {
      try { localStorage.setItem('storeActivationDismissed', 'true'); } catch (_) {}
    });
  };

  const handleDismissLater = () => {
    // Plain "Don't show again" link click — same permanent
    // dismiss as the X. (Previously this was "dismiss for today",
    // but daily resurfacing of a celebration card is what the
    // merchant complained about after long sign-ins.)
    closeWithAnimation(() => {
      try { localStorage.setItem('storeActivationDismissed', 'true'); } catch (_) {}
    });
  };

  return (
    <div
      className={`activation-card${isVisible ? ' visible' : ''}`}
      role="dialog"
      aria-label={`${storeName} is live — share your store`}
    >
      <div className="activation-head">
        <span className="activation-status" aria-label="Store is live">
          <span className="activation-status-dot" aria-hidden="true" />
          Live
        </span>
        <button
          type="button"
          className="activation-close"
          onClick={handleClose}
          aria-label="Don't show again"
          title="Don't show again"
        >
          <X size={16} strokeWidth={2.25} />
        </button>
      </div>

      <h2 className="activation-title">Your store is live</h2>

      <div className="activation-url">
        <Link2 size={14} className="activation-url-icon" aria-hidden="true" />
        <span className="activation-url-text" title={storeUrl}>{displayUrl}</span>
        <button
          type="button"
          className="activation-copy"
          onClick={handleCopyLink}
          aria-label="Copy store link"
        >
          {copied ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2} />}
        </button>
      </div>

      <span className="activation-share-label">Share via</span>
      <div className="activation-share-row">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="activation-share-btn whatsapp"
          aria-label="Share on WhatsApp"
          title="Share on WhatsApp"
        >
          <WhatsAppIcon />
        </a>
        <a
          href={twitterUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="activation-share-btn twitter"
          aria-label="Share on X"
          title="Share on X"
        >
          <TwitterIcon />
        </a>
        <a
          href={facebookUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="activation-share-btn facebook"
          aria-label="Share on Facebook"
          title="Share on Facebook"
        >
          <FacebookIcon />
        </a>
      </div>

      <a
        href={storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="activation-visit"
      >
        Visit store
        <ArrowUpRight size={16} strokeWidth={2.25} />
      </a>

      <button
        type="button"
        className="activation-dismiss"
        onClick={handleDismissLater}
      >
        Don't show again
      </button>
    </div>
  );
};

export default StoreActivationSidebar;
